import React, { useState, useRef, FormEvent, useEffect, useCallback } from "react";

import { DeviceEnum } from "../../types/context-detection";
import { DeviceScrollEnum } from "../../types/suggestions";
import { IHandleKeyboardNavigation } from "../../types/keyboard-navigation";
import { ISuggestionResponse } from "../../types/suggestion-response";
import { SearchBoxProps } from "../../types/searchbox";

import { L10nContext, NavigationContext } from "../common/contexts";
import { PageViewLogDataId } from "../../util/pageViewLogDataId";
import { useKeyboardNavigation } from "../common/keyboard-navigation";
import { useL10n } from "../common/l10n";
import debounce from "../../util/debounce";
import defaultSuggestionResponse from "../../util/defaultSuggestionResponse";
import Modal from "../modal/modal";
import SearchIcon from "../../Assets/svg/Search_2021_icon";
import SuggestionColumns from "../suggestion-list/suggestion-columns";

import "./searchbox-wide.scss";
import "../suggestion-list/suggestions-wide.scss";

const SearchBoxWide = ({ detectedContext, ...props }: SearchBoxProps) => {
    const { isWebShop, isMobileShop, device, customerType } = detectedContext;
    const SEARCH_TERM_LENGTH_LIMIT = 50;
    const TOP_SEARCH_QUERIES_LIMIT = isWebShop ? 10 : 5;
    const inputTabNavAttribute = { focusable: true };

    const [focus, setFocus] = useState(false);
    const [input, setInput] = useState("");
    const [validSearchTerm, setValidSearchTerm] = useState("");
    const [suggestions, setSuggestions] = useState<ISuggestionResponse>(
        defaultSuggestionResponse()
    );
    const [hasSuggestions, setHasSuggestions] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [deviceScrollClass, setDeviceScrollClass] = useState(DeviceScrollEnum.None);
    const [showModal, setShowModal] = useState(false);

    const searchBoxRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const focusRef = useRef(focus);
    const touchScrollElementRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const keyboardNavElementRef = useRef<IHandleKeyboardNavigation | null>(null);

    const pageViewLogDataId = PageViewLogDataId.get();

    const l10n = useL10n(props.l10n);

    useEffect(() => {
        focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
        const hasSuggestions =
            suggestions.articles.length > 0 ||
            suggestions.flexibleSets.length > 0 ||
            suggestions.contents.length > 0 ||
            suggestions.topSearchQueries.length > 0;
        setHasSuggestions(hasSuggestions);
    }, [suggestions]);

    useEffect(() => {
        const showSuggestions = hasSuggestions && focus && !!validSearchTerm;
        setShowSuggestions(showSuggestions);
    }, [hasSuggestions, focus, validSearchTerm]);

    useEffect(() => {
        if (showSuggestions && touchScrollElementRef.current) {
            // enables touch scroll on parallax pages
            if (device === DeviceEnum.Kiosk || device === DeviceEnum.Tablet) {
                touchScrollElementRef.current.ontouchstart = (e) => e.stopPropagation();
                touchScrollElementRef.current.ontouchend = (e) => e.stopPropagation();
                touchScrollElementRef.current.ontouchmove = (e) => e.stopPropagation();
            }
        }
    }, [showSuggestions, device]);

    const handleClick = useCallback((event: Event) => {
        const target = event.target as HTMLElement;

        if (searchBoxRef.current && !searchBoxRef.current.contains(target) && focusRef.current) {
            handleBlur();
        }
    }, []);

    useEffect(() => {
        if (DeviceEnum.Kiosk === device) {
            setDeviceScrollClass(DeviceScrollEnum.Kiosk);
        } else if (DeviceEnum.Tablet === device) {
            setDeviceScrollClass(DeviceScrollEnum.Tablet);
        } else if (DeviceEnum.Desktop === device) {
            setDeviceScrollClass(DeviceScrollEnum.Desktop);
        }
    }, [device]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClick);

        return () => {
            document.removeEventListener("mousedown", handleClick);
        };
    }, [handleClick]);

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement> | KeyboardEvent) => {
            e.preventDefault();

            const trimmedInput = input.trim();
            if (!trimmedInput) {
                handleBlur();
                setShowModal(true);
                setShowSuggestions(false);
                return;
            }

            window.location.href = `${props.searchTarget}?query=${encodeURIComponent(
                trimmedInput
            )}`;
        },
        [input, props.searchTarget]
    );

    const navigationApi = useKeyboardNavigation(
        () => keyboardNavElementRef.current?.getNavigationElements() ?? []
    );

    const handleKeyPressedWhileFocused = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Tab" || (event.shiftKey && event.key === "Tab")) {
                handleBlur();
            } else if (
                (event.key === "Escape" || event.key === "Esc") &&
                keyboardNavElementRef.current !== null
            ) {
                handleBlur();
                shell?.tabNav.focusNext();
            } else if (event.key === "Enter" && !navigationApi.hasSelectedNavElement()) {
                handleSubmit(event);
            } else if (device === DeviceEnum.Desktop) {
                navigationApi.handleKeyDown(event);
            }
        },
        [device, navigationApi, handleSubmit]
    );

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;

        if (focus) {
            input.addEventListener("keydown", handleKeyPressedWhileFocused);
        } else {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        }

        return () => {
            input.removeEventListener("keydown", handleKeyPressedWhileFocused);
        };
    }, [focus, handleKeyPressedWhileFocused]);

    const handleModalKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (showModal && (event.key === "Escape" || event.key === "Esc")) {
                event.preventDefault();
                setShowModal(false);
                inputRef.current?.focus();
            }
        },
        [showModal]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleModalKeyDown);

        return () => {
            document.removeEventListener("keydown", handleModalKeyDown);
        };
    }, [handleModalKeyDown]);

    useEffect(() => {
        const suggestionsVisible = hasSuggestions && showSuggestions;
        if (!suggestionsVisible) {
            navigationApi.resetNavigation();
        }
    }, [hasSuggestions, showSuggestions, navigationApi]);

    const resetSuggestionResponse = () => {
        setSuggestions(defaultSuggestionResponse());
    };

    const fetchSuggestions = async (input: string) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const headers: Record<string, string> = pageViewLogDataId
            ? { "X-PageView-ID": pageViewLogDataId }
            : {};

        try {
            const res = await fetch(
                `${props.apiUrl}suggestion/?searchTerm=${encodeURIComponent(
                    input
                )}&topSearchQueriesLimit=${TOP_SEARCH_QUERIES_LIMIT}`,
                { headers, signal: controller.signal }
            );

            if (!res.ok) throw new Error("Network response was not ok");

            const response = await res.json();

            if (
                response.articles.length ||
                response.flexibleSets.length ||
                response.contents.length ||
                response.topSearchQueries.length
            ) {
                setSuggestions(response);
            } else {
                resetSuggestionResponse();
            }
        } catch (error) {
            if (error instanceof Error && error.name !== "AbortError") {
                resetSuggestionResponse();
            }
        } finally {
            abortControllerRef.current = null;
        }
    };

    const debouncedFetch = useRef<((input: string) => void) & { cancel: () => void }>(
        debounce((input: string) => {
            setValidSearchTerm(input);
            fetchSuggestions(input);
        }, 250)
    );

    const sanatizeInput = (input: string) => {
        const validInput = input.length > SEARCH_TERM_LENGTH_LIMIT ? input.slice(0, 50) : input;
        return validInput;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value ?? "";
        const sanatizedInput = sanatizeInput(value);
        setInput(sanatizedInput);

        const trimmedInput = sanatizedInput.trim();
        if (!trimmedInput) {
            debouncedFetch.current.cancel();
            setValidSearchTerm("");
            resetSuggestionResponse();
        } else {
            debouncedFetch.current(trimmedInput);
        }
    };

    const handleFocus = () => {
        setFocus(true);
    };

    const hideModal = () => {
        setShowModal(false);
        inputRef.current?.focus();
    };

    function handleBlur(): void {
        setFocus(false);
        inputRef.current?.blur();
    }

    return (
        <L10nContext.Provider value={l10n}>
            <div
                className={`searchbox wide ${props.tenant} ${focus ? "focus" : ""}`}
                ref={searchBoxRef}
            >
                <div className="search">
                    <form
                        method="get"
                        action={props.searchTarget}
                        acceptCharset="utf-8"
                        onSubmit={handleSubmit}
                    >
                        <input
                            ref={inputRef}
                            value={input}
                            maxLength={50}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            placeholder={validSearchTerm || focus ? "" : l10n.defaultTerm}
                            className="ssw-input"
                            type="text"
                            name="query"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="none"
                            spellCheck="false"
                            aria-label={l10n.defaultTerm}
                            {...inputTabNavAttribute}
                        />
                        <button className="ssw-button" type="submit" aria-label={l10n.defaultTerm}>
                            <SearchIcon />
                        </button>
                    </form>
                </div>

                {showSuggestions && (
                    <NavigationContext.Provider value={navigationApi}>
                        <div className={"ssw-backdrop"} onClick={handleBlur}></div>
                        <div
                            id={`new-suggestions-${props.view}`}
                            className={"new-suggestions"}
                            ref={touchScrollElementRef}
                        >
                            <SuggestionColumns
                                navigationRef={keyboardNavElementRef}
                                portal={props.portal}
                                culture={props.culture}
                                searchTarget={props.searchTarget}
                                trackingEndpoint={props.trackingEndpoint}
                                articles={suggestions.articles}
                                flexibleSets={suggestions.flexibleSets}
                                contents={suggestions.contents}
                                featurePromotion={suggestions.featurePromotion}
                                topSearchQueries={suggestions.topSearchQueries}
                                priceMandantSettings={suggestions.priceMandantSettingsArticles}
                                priceMandantSettingsV2={suggestions.priceMandantSettingsFlexibleSet}
                                customerType={customerType}
                                searchTerm={validSearchTerm}
                                isMobileShop={isMobileShop}
                                view={props.view}
                                pageViewLogDataId={pageViewLogDataId}
                                deviceScrollClass={deviceScrollClass}
                                deviceType={device}
                            />
                        </div>
                    </NavigationContext.Provider>
                )}

                {isWebShop && (
                    <Modal
                        open={showModal}
                        parentName={props.view}
                        tenant={props.tenant}
                        onClose={() => hideModal()}
                    />
                )}
            </div>
        </L10nContext.Provider>
    );
};

export default SearchBoxWide;
